import { useState, useEffect, useCallback } from 'react';
import { Tooltip as MuiTooltip, useTheme } from '@mui/material';
import type { TooltipProps as MuiTooltipProps } from '@mui/material';
import { colors } from 'styles/theme/colors';

// Global state to block all tooltips during scroll
let isScrolling = false;
let scrollEndTimer: ReturnType<typeof setTimeout> | null = null;

const handleGlobalScroll = () => {
  isScrolling = true;

  // Clear existing timer
  if (scrollEndTimer) clearTimeout(scrollEndTimer);

  // Reset after scrolling stops (debounce 150ms)
  scrollEndTimer = setTimeout(() => {
    isScrolling = false;
  }, 150);
};

// Add global listeners once (outside component)
if (typeof document !== 'undefined') {
  document.addEventListener('wheel', handleGlobalScroll, { passive: true });
  document.addEventListener('scroll', handleGlobalScroll, { passive: true, capture: true });
}

/** Popper modifier config - typed so the modifiers array is mutable for MUI compatibility. */
type PopperModifier = { name: string; options?: Record<string, unknown> };

// Keep long free-text tooltips (wrapLongText) inside the viewport: flip to an
// opposite edge when needed and shift along the cross axis so the wrapped block
// never clips. Mirrors the convention in multiselectInlineEditStyles.ts.
const WRAP_TOOLTIP_POPPER_MODIFIERS: PopperModifier[] = [
  {
    name: 'flip',
    options: { fallbackPlacements: ['top', 'bottom', 'right', 'left'] },
  },
  {
    name: 'preventOverflow',
    options: { boundary: 'viewport', altAxis: true, padding: 8 },
  },
];

export type TooltipProps = Pick<
  MuiTooltipProps,
  | 'title'
  | 'sx'
  | 'children'
  | 'arrow'
  | 'disableInteractive'
  | 'disableFocusListener'
  | 'disableHoverListener'
  | 'followCursor'
  | 'id'
  | 'onClose'
  | 'onOpen'
  | 'open'
  | 'placement'
  | 'slots'
  | 'slotProps'
  | 'enterDelay'
  | 'enterNextDelay'
> & {
  /** Render with the error palette (red background + border, error-foreground text). */
  error?: boolean;
  /**
   * Opt-in for long free-text tooltips (e.g. table cell descriptions): wraps text,
   * caps width/height, anchors to the element (not the cursor), and keeps the popper
   * inside the viewport. Off by default so short label tooltips are unchanged.
   */
  wrapLongText?: boolean;
};

/**
 * Tooltip component, part of Delogue design system.
 * @param title - the content to display in the tooltip
 * @param children - the element that triggers the tooltip on hover
 * @param arrow - whether to display an arrow pointing to the trigger element, default is false
 * @param disableInteractive - if true, the tooltip won't be interactive and will close when the user hovers over it, default is false
 * @param placement - the placement of the tooltip relative to the trigger element
 * @param onOpen - callback function to be called when the tooltip opens
 * @param onClose - callback function to be called when the tooltip closes
 * @param slots - custom slot components to override default tooltip parts
 * @param slotProps - additional props to pass to the slot components
 */
export const Tooltip = ({
  sx,
  arrow = false,
  title,
  children,
  placement,
  disableInteractive = false,
  disableFocusListener = false,
  disableHoverListener = false,
  followCursor = true,
  onOpen,
  onClose,
  open: openProp,
  slotProps,
  slots,
  enterDelay,
  enterNextDelay,
  error = false,
  wrapLongText = false,
}: TooltipProps) => {
  const theme = useTheme();
  const tooltipBg = error ? theme.palette.bg.errorPrimary : colors.grey[600];
  const tooltipFg = error ? theme.palette.fg.errorPrimary : undefined;
  // Long free-text tooltips must anchor to the element (not the cursor) so the
  // popper can flip/shift predictably within the viewport.
  const effectiveFollowCursor = wrapLongText ? false : followCursor;
  const [openState, setOpenState] = useState(false);

  const isControlled = openProp !== undefined;
  const effectiveOpen = isControlled ? openProp : openState;

  // Force-close when title becomes empty (e.g. entering edit mode) so tooltip does not stay stuck
  useEffect(() => {
    if (!isControlled && !title && openState) setOpenState(false);
  }, [isControlled, title, openState]);

  // Close tooltip when user scrolls or wheels (so onClose is called as tests expect)
  useEffect(() => {
    if (!effectiveOpen) return;
    const closeOnScroll = () => {
      if (!isControlled) setOpenState(false);
      onClose?.({} as React.SyntheticEvent);
    };
    document.addEventListener('wheel', closeOnScroll, { passive: true });
    document.addEventListener('scroll', closeOnScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('wheel', closeOnScroll);
      document.removeEventListener('scroll', closeOnScroll);
    };
  }, [effectiveOpen, isControlled, onClose]);

  const handleOpen = useCallback(
    (event: React.SyntheticEvent) => {
      if (isScrolling) return; // Block ALL tooltips during scroll
      if (!isControlled) setOpenState(true);
      onOpen?.(event);
    },
    [isControlled, onOpen]
  );

  const handleClose = useCallback(
    (event: React.SyntheticEvent | Event) => {
      if (!isControlled) setOpenState(false);
      onClose?.(event);
    },
    [isControlled, onClose]
  );

  return (
    <MuiTooltip
      title={title}
      sx={{ marginTop: 0, ...sx }}
      arrow={arrow}
      onOpen={handleOpen}
      onClose={handleClose}
      open={effectiveOpen}
      placement={placement}
      disableInteractive={disableInteractive}
      disableFocusListener={disableFocusListener}
      disableHoverListener={disableHoverListener}
      slots={slots}
      followCursor={effectiveFollowCursor}
      enterDelay={enterDelay}
      enterNextDelay={enterNextDelay}
      leaveDelay={0}
      slotProps={{
        arrow: { sx: { color: tooltipBg } },
        tooltip: {
          sx: {
            background: tooltipBg,
            ...(tooltipFg && { color: tooltipFg }),
            fontSize: error ? '0.75rem' : theme.typography.smallTitle.fontSize,
            lineHeight: error ? 1.4 : theme.typography.smallTitle.lineHeight,
            paddingX: theme.spacing(3),
            paddingY: theme.spacing(2),
            transition: 'none !important',
            // Long free-text: wrap into a readable block and cap height so even a
            // ~1000-char description never exceeds the viewport. The tooltip is
            // non-interactive (can't be scrolled into), so clamp rather than scroll —
            // preventOverflow keeps the whole block on-screen.
            ...(wrapLongText && {
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              maxWidth: 360,
              maxHeight: '60vh',
              overflow: 'hidden',
            }),
          },
        },
        popper: {
          ...(wrapLongText && {
            popperOptions: { modifiers: WRAP_TOOLTIP_POPPER_MODIFIERS },
          }),
          sx: {
            transition: 'none !important',
            // Tighten the gap between anchor and tooltip in error mode.
            // MUI's default placement margin with arrow is ~14px; shrink to 1px.
            ...(error && {
              '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip': { marginTop: '5px' },
              '&[data-popper-placement*="top"] .MuiTooltip-tooltip': { marginBottom: '1px' },
              '&[data-popper-placement*="left"] .MuiTooltip-tooltip': { marginRight: '1px' },
              '&[data-popper-placement*="right"] .MuiTooltip-tooltip': { marginLeft: '1px' },
            }),
          },
        },
        transition: { timeout: { enter: 200, exit: 50 } },
        ...slotProps,
      }}
    >
      {children}
    </MuiTooltip>
  );
};
